/**
 * DeviceConnectionStep Component
 *
 * Step 2: Connect to the G5 device via WebHID
 * Guides user through device connection with browser permission flow.
 */

import { Button } from '@ui/shared/Button';
import * as React from 'react';
import styled, { keyframes } from 'styled-components';
import { KEYS } from '../../../../../keys';
import { Icon } from '../../../../shared/Icon';
import { useUpdaterServices } from '../../hooks/useUpdaterServices';
import { useWizard } from '../../hooks/useWizard';
import { IMX_DEVICES, IMXDeviceInfo } from '../../types/imx-types';
import {
  DeviceInfo,
  ErrorCode,
  RecoveryAction,
  StateActionType,
  UpdaterError,
  WizardStep,
} from '../../types/wizard-types';
import { TroubleshootingSection } from '../TroubleshootingSection';
import { WarningBanner } from '../WarningBanner';

/**
 * Check if WebHID is supported
 */
const isWebHIDSupported = (): boolean => {
  return 'hid' in navigator;
};

/**
 * DeviceConnectionStep component
 */
export const DeviceConnectionStep: React.FC = (): React.ReactElement => {
  const { setDeviceInfo, setError, dispatch, skipToStep } = useWizard();
  const { setHidDevice, logger } = useUpdaterServices();

  const [isConnecting, setIsConnecting] = React.useState(false);
  const [connectionStatus, setConnectionStatus] = React.useState<
    'idle' | 'requesting' | 'connected' | 'error'
  >('idle');
  // Track if device is in U-Boot mode (resuming interrupted update)
  const [isResuming, setIsResuming] = React.useState(false);
  // Track if user attempted connection but no device was found
  const [showTroubleshootingHelp, setShowTroubleshootingHelp] =
    React.useState(false);

  // Ref for scrolling to troubleshooting section
  const troubleshootingRef = React.useRef<HTMLDivElement>(null);

  // Check browser support on mount
  const isSupported = isWebHIDSupported();

  /**
   * Auto-skip to Easy Installer step when resuming
   * This effect triggers when a U-Boot device is detected, indicating
   * the bootloader was already transferred in a previous session
   */
  React.useEffect((): (() => void) | void => {
    if (isResuming && connectionStatus === 'connected') {
      // Give user a moment to see the "resuming" message before auto-advancing
      const timer = setTimeout((): void => {
        logger.info('Auto-skipping to Easy Installer step (resume mode)');
        // Mark step 2 (device connection) and step 3 (bootloader) as completed, then skip to step 4
        skipToStep(WizardStep.EASY_INSTALLER_LOADING, [
          WizardStep.DEVICE_CONNECTION,
          WizardStep.BOOTLOADER_TRANSFER,
        ]);
      }, 1500);

      return (): void => clearTimeout(timer);
    }
  }, [isResuming, connectionStatus, skipToStep, logger]);

  /**
   * Request device connection via WebHID
   */
  const handleConnectDevice = async (): Promise<void> => {
    if (!isSupported) {
      setError(
        new UpdaterError(
          'Your browser does not support WebHID. Please use Chrome or Edge to update your device.',
          ErrorCode.BROWSER_NOT_SUPPORTED,
          WizardStep.DEVICE_CONNECTION,
          [
            {
              action: RecoveryAction.CONTACT_SUPPORT,
              label: 'Get Help',
              description: 'Contact support for alternative update methods',
              order: 1,
            },
          ]
        )
      );
      return;
    }

    setIsConnecting(true);
    setConnectionStatus('requesting');

    try {
      // Build device filter for WebHID
      const filters = IMX_DEVICES.map(
        (d: IMXDeviceInfo): { vendorId: number; productId: number } => ({
          vendorId: d.vid,
          productId: d.pid,
        })
      );

      // Request device from user
      const devices = await navigator.hid.requestDevice({ filters });

      if (!devices || devices.length === 0) {
        // User cancelled the dialog or no device found - show troubleshooting help
        setConnectionStatus('idle');
        setShowTroubleshootingHelp(true);
        // Scroll to troubleshooting section after a brief delay
        setTimeout(() => {
          troubleshootingRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        }, 100);
        return;
      }

      const device = devices[0];

      // Find matching device config
      const deviceConfig = IMX_DEVICES.find(
        (d: IMXDeviceInfo): boolean =>
          d.vid === device.vendorId && d.pid === device.productId
      );

      if (!deviceConfig) {
        setConnectionStatus('error');
        setError(
          new UpdaterError(
            'The selected device is not recognized as a supported device.',
            ErrorCode.WRONG_DEVICE,
            WizardStep.DEVICE_CONNECTION,
            [
              {
                action: RecoveryAction.RETRY_STEP,
                label: 'Select Different Device',
                description: 'Connect and select the correct device',
                order: 1,
              },
              {
                action: RecoveryAction.CONTACT_SUPPORT,
                label: 'Contact Support',

                description: 'Get help identifying your device',
                order: 2,
              },
            ]
          )
        );
        return;
      }

      // Open the device
      await device.open();

      // Store the HID device in services context for later use
      setHidDevice(device);
      logger.info(
        `Device opened: VID=0x${device.vendorId.toString(
          16
        )}, PID=0x${device.productId.toString(16)}`
      );

      // Determine device stage (ROM mode or U-Boot)
      const stage = deviceConfig.mode === 'ROM' ? 'rom' : 'uboot';
      const isUBootMode = deviceConfig.mode === 'U-Boot';

      // Create device info
      const deviceInfo: DeviceInfo = {
        vendorId: device.vendorId,
        productId: device.productId,
        productName: device.productName || deviceConfig.name,
        stage,
        config: deviceConfig,
      };

      setDeviceInfo(deviceInfo);
      setConnectionStatus('connected');

      // If device is in U-Boot mode, we're resuming an interrupted update
      if (isUBootMode) {
        setIsResuming(true);
        logger.info('U-Boot device detected - resuming interrupted update');
      }

      // Mark as connected
      dispatch({ type: StateActionType.SET_DEVICE_CONNECTED, connected: true });
      logger.success(
        `Device connected successfully: ${deviceInfo.productName} (${stage} mode)`
      );
    } catch (err: unknown) {
      setConnectionStatus('error');

      const errorName = err instanceof Error ? err.name : '';
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      const errorStack = err instanceof Error ? err.stack : undefined;

      // Handle specific errors
      if (errorName === 'NotFoundError') {
        setError(
          new UpdaterError(
            'No G5 device was found. Make sure your device is connected via USB and in recovery mode.',
            ErrorCode.DEVICE_NOT_DETECTED,
            WizardStep.DEVICE_CONNECTION,
            [
              {
                action: RecoveryAction.CHECK_CONNECTION,
                label: 'Check Connection',
                description: 'Verify USB cable is properly connected',
                order: 1,
              },
              {
                action: RecoveryAction.POWER_CYCLE,
                label: 'Enter Recovery Mode',
                description:
                  'Power cycle device while holding the recovery button',
                order: 2,
              },
              {
                action: RecoveryAction.RETRY_STEP,
                label: 'Try Again',
                description: 'Attempt to connect again',
                order: 3,
              },
            ]
          )
        );
      } else if (errorName === 'SecurityError') {
        setError(
          new UpdaterError(
            'Permission denied. Please allow access to the HID device when prompted.',
            ErrorCode.DEVICE_OPEN_FAILED,
            WizardStep.DEVICE_CONNECTION,
            [
              {
                action: RecoveryAction.RETRY_STEP,
                label: 'Try Again',
                description: 'Click Connect and allow device access',
                order: 1,
              },
            ]
          )
        );
      } else {
        setError(
          new UpdaterError(
            `Failed to connect to device: ${errorMessage}`,
            ErrorCode.DEVICE_OPEN_FAILED,
            WizardStep.DEVICE_CONNECTION,
            [
              {
                action: RecoveryAction.RETRY_STEP,
                label: 'Try Again',
                description: 'Attempt to connect again',
                order: 1,
              },
              {
                action: RecoveryAction.POWER_CYCLE,
                label: 'Power Cycle Device',
                description: 'Disconnect and reconnect the device',
                order: 2,
              },
            ],
            errorStack
          )
        );
      }
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <StepContainer data-testid={KEYS.WIZARD_DEVICE_STEP}>
      {/* Browser Support Warning */}
      {!isSupported && (
        <WarningBanner
          title='Browser Not Supported'
          message='WebHID is required for device updates. Please use Google Chrome or Microsoft Edge.'
        />
      )}

      {/* Instructions Card */}
      <InstructionCard>
        <InstructionHeader>
          <InstructionIcon>
            <Icon name='usb' size='small' color='white' />
          </InstructionIcon>
          <InstructionHeaderText>
            <InstructionTitle data-testid={KEYS.WIZARD_DEVICE_HEADING}>
              Connect Your Force Plates
            </InstructionTitle>
            <InstructionDescription>
              Follow these steps to connect your Hawkin Force Plates for
              updating:
            </InstructionDescription>
          </InstructionHeaderText>
        </InstructionHeader>

        <InstructionSteps>
          <InstructionStep>
            <StepNumber>1</StepNumber>
            <StepContent>
              <StepTitle>Connect USB Cable</StepTitle>
              <StepDescription>
                Connect your Force Plates to this computer using a USB-C cable.
                Depending on your system, you may require a USB-A to USB-C cable
                or conversion dongle.
              </StepDescription>
              <StepNote>
                <Icon
                  name='info'
                  size='small'
                  color='rgba(255, 255, 255, 0.56)'
                />
                The Force Plates do not need to be connected to each other to
                complete this update.
              </StepNote>
            </StepContent>
          </InstructionStep>

          <InstructionStep>
            <StepNumber>2</StepNumber>
            <StepContent>
              <StepTitle>Enter Recovery Mode</StepTitle>
              <RecoveryModeContent>
                <StepDescription>
                  <ol>
                    <li>
                      Locate the recovery button on your plates notated by the
                      dotted circle on the front of the plates.
                    </li>
                    <li>
                      Using a pen or SIM tool, <strong>hold</strong> the
                      recovery button until you hear a click.
                    </li>
                    <li>
                      While holding the recovery button,{' '}
                      <strong>press the power button.</strong>
                    </li>
                    <li>
                      Release the recovery button. Note: the plates will
                      continue to run through the 4 light booting sequence.
                    </li>
                  </ol>
                </StepDescription>
                <VideoContainer>
                  <VideoIframe
                    src='https://www.youtube.com/embed/y28S_WzXwtg'
                    title='How to enter recovery mode'
                    allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                    allowFullScreen
                  />
                </VideoContainer>
              </RecoveryModeContent>
            </StepContent>
          </InstructionStep>

          <InstructionStep>
            <StepNumber>3</StepNumber>
            <StepContent>
              <StepTitle>Click Connect To Plate</StepTitle>
              <StepDescription>
                Click the button and select the item that contains &quot;SE
                Blank 6ULL&quot; from the list. Then click &quot;Connect&quot;
                from the browser dialog. The charging light will flash during
                the connection process.
              </StepDescription>
              {connectionStatus !== 'connected' && (
                <StepButtonRow>
                  {isConnecting ? (
                    <Button title='Connecting...' flavor='primary'>
                      <SpinningIcon />
                    </Button>
                  ) : (
                    <Button
                      icon='usb'
                      title='Connect to Plate'
                      onClick={handleConnectDevice}
                      disabled={!isSupported || isConnecting}
                      data-testid={KEYS.WIZARD_CONNECT_BUTTON}
                    />
                  )}
                </StepButtonRow>
              )}
            </StepContent>
          </InstructionStep>
        </InstructionSteps>
      </InstructionCard>

      {/* Connected message - different for normal vs resume mode */}
      {connectionStatus === 'connected' && !isResuming && (
        <ConnectedMessage data-testid={KEYS.WIZARD_DEVICE_CONNECTED}>
          <Icon name='check_circle' size='medium' color='#66bb6a' />
          <span>
            Device connected. Click <strong>Continue</strong> to proceed.
          </span>
        </ConnectedMessage>
      )}

      {/* Resume mode message */}
      {connectionStatus === 'connected' && isResuming && (
        <ResumeMessage>
          <Icon
            name='fast_forward'
            size='medium'
            color='var(--color-primary)'
          />
          <ResumeContent>
            <ResumeTitle>Resuming Interrupted Update</ResumeTitle>
            <ResumeDescription>
              Your device is ready to continue from where it left off. Click{' '}
              <strong>Continue</strong> to resume the update process.
            </ResumeDescription>
          </ResumeContent>
        </ResumeMessage>
      )}

      {/* Help Section */}
      <TroubleshootingSection
        ref={troubleshootingRef}
        expanded={showTroubleshootingHelp}
        highlighted={showTroubleshootingHelp}
      />
    </StepContainer>
  );
};

// Animations

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// Styled Components

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InstructionCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  background: var(--color-surface-1);
  border-radius: 10px;
`;

const InstructionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

const InstructionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: var(--color-primary);
  border-radius: 12px;
  flex-shrink: 0;
`;

const InstructionHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InstructionTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text);
`;

const InstructionDescription = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.72);
`;

const InstructionSteps = styled.div`
  display: flex;
  flex-direction: column;
`;

const InstructionStep = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:first-child {
    padding-top: 0;
  }
`;

const StepNumber = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: var(--color-primary);
  border-radius: 50%;
  font-size: 16px;
  font-weight: bold;
  color: white;
  flex-shrink: 0;
`;

const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StepTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
`;

const StepDescription = styled.span`
  font-size: 13px;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.72);
`;

const StepNote = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 12px;
  font-style: italic;
  color: rgba(255, 255, 255, 0.56);
`;

const RecoveryModeContent = styled.div`
  display: flex;
  gap: 20px;
  align-items: flex-start;
`;

const VideoContainer = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 280px;
  aspect-ratio: 16 / 9;
  border-radius: 8px;
  overflow: hidden;
  background: var(--color-surface-2);
`;

const VideoIframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
`;

const StepButtonRow = styled.div`
  margin-top: 12px;
`;

const SpinningIcon = styled.span`
  display: inline-flex;
  animation: ${spin} 1s linear infinite;
`;

const ConnectedMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 20px;
  background: var(--color-surface-1);
  border-radius: 10px;
  font-size: 16px;
  color: #66bb6a;

  strong {
    color: #66bb6a;
  }
`;

const ResumeMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: var(--color-surface-1);
  border: 2px solid var(--color-primary);
  border-radius: 10px;
`;

const ResumeContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ResumeTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: var(--color-primary);
`;

const ResumeDescription = styled.span`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.72);

  strong {
    color: var(--color-text);
  }
`;

export default DeviceConnectionStep;
